/**
 * Shown when ?formUid= points to a locked form (submitted, verified, finalized, etc.).
 * Patient can only start a fresh intake — editing is blocked intentionally.
 */
import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CheckCircle2 } from "lucide-react";
import type { IntakeData } from "@/lib/types";
import { Button } from "@/components/ui/button";

interface SubmittedFormBarrierProps {
  data: Pick<
    IntakeData,
    "referenceUid" | "formUid"
  > & {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  onStartNewIntake: () => void;
}

export function SubmittedFormBarrier({
  data,
  onStartNewIntake,
}: SubmittedFormBarrierProps) {
  const { _ } = useLingui();
  const patientName =
    `${data.firstName?.trim() ?? ""} ${data.lastName?.trim() ?? ""}`.trim() ||
    data.email?.trim() ||
    "—";

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white/90 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)] backdrop-blur-sm">
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" aria-hidden />
          </div>
        </div>

        <h1 className="text-center text-2xl font-semibold text-gray-900">
          {_(msg`This intake form has already been submitted`)}
        </h1>

        <p className="mt-3 text-center text-base leading-relaxed text-gray-600">
          <Trans>
            We found an existing submission for this form. You cannot continue
            editing it here, but you can start a new intake if needed.
          </Trans>
        </p>

        <dl className="mt-6 space-y-3 rounded-xl bg-gray-50 px-4 py-4 text-sm">
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
            <dt className="font-medium text-gray-500">{_(msg`Patient`)}</dt>
            <dd className="text-gray-900">{patientName}</dd>
          </div>
          {data.email ? (
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
              <dt className="font-medium text-gray-500">{_(msg`Email`)}</dt>
              <dd className="break-all text-gray-900">{data.email}</dd>
            </div>
          ) : null}
          {data.referenceUid ? (
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
              <dt className="font-medium text-gray-500">{_(msg`Reference`)}</dt>
              <dd className="font-mono text-gray-900">{data.referenceUid}</dd>
            </div>
          ) : null}
          {data.formUid ? (
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
              <dt className="font-medium text-gray-500">{_(msg`Form ID`)}</dt>
              <dd className="font-mono text-gray-900">{data.formUid}</dd>
            </div>
          ) : null}
        </dl>

        <div className="mt-8 flex flex-col gap-3">
          <Button
            type="button"
            className="h-12 w-full rounded-xl bg-[#FF914D] text-base font-semibold text-white hover:bg-[#e88345]"
            onClick={onStartNewIntake}
          >
            {_(msg`Start a new intake`)}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-xl"
            asChild
          >
            <a href={import.meta.env.VITE_JEMA_WEBSITE_URL || "https://jemaservices.com.co"}>
              {_(msg`Return to Jema Services`)}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
